import { type FC, useMemo, useState } from "react";

import type { ProviderProgressEntry } from "../../../hooks/useProviderProgress";

import { useClipboard } from "../../../hooks/useClipboard";
import { formatDateTime, formatRelativeTime } from "../../../utils/time";
import { Button } from "../../common/Button";
import { EmptyState } from "../../common/EmptyState";
import { Modal } from "../../common/Modal";
import { getColorClassForEvent } from "./eventColors";
import {
  enrichProviderProgressEvents,
  filterVisibleProviderProgressEvents,
} from "./eventDisplay";
import { EventIcon } from "./EventIcon";
import { getEventSummary } from "./eventSummary";
import {
  getStaticSystemInstructions,
  readString,
  type StaticSystemInstruction,
} from "./promptData";

type TranscriptMode = "all" | "filtered";

type TranscriptEntry =
  | {
      event: ReturnType<typeof enrichProviderProgressEvents>[number];
      kind: "event";
    }
  | { instruction: StaticSystemInstruction; kind: "static-system-instruction" };

interface ProviderProgressTranscriptModalProps {
  events: ProviderProgressEntry[];
  isEventTypeFilterActive?: boolean;
  onClose: () => void;
  streamLabel: string;
  visibleEventTypes?: ReadonlySet<string>;
}

function formatEventTypeLabel(eventType: string): string {
  return eventType.replace(":", "::");
}

function getInjectedMessageBody(
  event: ReturnType<typeof enrichProviderProgressEvents>[number],
): string | null {
  if (event.event_type !== "message:injected") {
    return null;
  }

  return readString(event.event_data.message);
}

function buildTranscriptText(entries: TranscriptEntry[]): string {
  return entries
    .map((entry, index) => {
      if (entry.kind === "static-system-instruction") {
        const { instruction } = entry;
        const parts = [
          `${String(index + 1).padStart(2, "0")}. ${formatDateTime(
            instruction.timestamp ?? instruction.receivedAt,
          )}`,
          "[static:system-instruction]",
          instruction.mode ? `mode ${instruction.mode}` : "mode-level",
        ];

        if (typeof instruction.stepNumber === "number") {
          parts.push(`step ${instruction.stepNumber}`);
        }

        return `${parts.join(" ")}\n${instruction.body}`;
      }

      const { event } = entry;
      const parts = [
        `${String(index + 1).padStart(2, "0")}. ${formatDateTime(
          event.timestamp ?? event.received_at,
        )}`,
        `[${event.event_type}]`,
      ];

      if (event.stepNumber !== null) {
        parts.push(`step ${event.stepNumber}`);
      }

      const summary = getEventSummary(event);
      const header = parts.join(" ");
      if (summary && Object.keys(event.event_data).length === 0) {
        return `${header}\n${summary}`;
      }

      if (summary) {
        return `${header}\n${summary}\n${JSON.stringify(event.event_data, null, 2)}`;
      }

      if (Object.keys(event.event_data).length > 0) {
        return `${header}\n${JSON.stringify(event.event_data, null, 2)}`;
      }

      return header;
    })
    .join("\n\n");
}

const TranscriptIcon: FC = () => (
  <svg
    className="h-4 w-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.75}
      d="M7 8h10M7 12h10M7 16h6M6 3h12a2 2 0 012 2v14l-4-2-4 2-4-2-4 2V5a2 2 0 012-2z"
    />
  </svg>
);

const StaticInstructionIcon: FC = () => (
  <svg
    className="h-4 w-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.75}
      d="M7 6h10M7 10h10M7 14h6m-7 7l-3-3 3-3m10 6l3-3-3-3M5 3h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2z"
    />
  </svg>
);

const TranscriptInstructionBlock: FC<{
  body: string;
  label: string;
  tone?: "amber" | "sky";
}> = ({ body, label, tone = "amber" }) => {
  const styles =
    tone === "amber"
      ? {
          container:
            "border-amber-200 bg-amber-50 dark:border-amber-500/30 dark:bg-slate-900/70",
          header:
            "border-amber-200 text-amber-700 dark:border-amber-500/30 dark:text-amber-200",
        }
      : {
          container:
            "border-slate-300 bg-slate-50 dark:border-slate-700 dark:bg-slate-900/70",
          header:
            "border-slate-300 text-slate-700 dark:border-slate-700 dark:text-emerald-200",
        };

  return (
    <div className={`overflow-hidden border ${styles.container}`}>
      <div
        className={`border-b px-3 py-2 font-mono text-[10px] font-bold uppercase tracking-[0.18em] ${styles.header}`}
      >
        {label}
      </div>
      <pre className="custom-scrollbar overflow-auto whitespace-pre-wrap break-words bg-white p-3 font-mono text-[10px] leading-relaxed text-slate-700 dark:bg-arti-panel dark:text-slate-200">
        {body}
      </pre>
    </div>
  );
};

export const ProviderProgressTranscriptModal: FC<
  ProviderProgressTranscriptModalProps
> = ({
  events,
  isEventTypeFilterActive = false,
  onClose,
  streamLabel,
  visibleEventTypes,
}) => {
  const [mode, setMode] = useState<TranscriptMode>(
    isEventTypeFilterActive ? "filtered" : "all",
  );
  const [includeStaticSystemInstructions, setIncludeStaticSystemInstructions] =
    useState(false);
  const { copy, status } = useClipboard();

  const enrichedEvents = useMemo(
    () => enrichProviderProgressEvents(events),
    [events],
  );
  const filteredEvents = useMemo(
    () =>
      filterVisibleProviderProgressEvents(enrichedEvents, visibleEventTypes),
    [enrichedEvents, visibleEventTypes],
  );
  const transcriptEvents =
    mode === "filtered" ? filteredEvents : enrichedEvents;
  const staticSystemInstructions = useMemo(
    () => getStaticSystemInstructions(events),
    [events],
  );
  const transcriptEntries = useMemo<TranscriptEntry[]>(() => {
    const staticEntries = includeStaticSystemInstructions
      ? staticSystemInstructions.map((instruction) => ({
          instruction,
          kind: "static-system-instruction" as const,
        }))
      : [];
    const eventEntries = transcriptEvents.map((event) => ({
      event,
      kind: "event" as const,
    }));

    return [...staticEntries, ...eventEntries];
  }, [
    includeStaticSystemInstructions,
    staticSystemInstructions,
    transcriptEvents,
  ]);
  const transcriptText = useMemo(
    () => buildTranscriptText(transcriptEntries),
    [transcriptEntries],
  );
  const transcriptMeta = [
    mode === "filtered"
      ? "Current event filter applied"
      : "All visible stream events",
    includeStaticSystemInstructions && staticSystemInstructions.length > 0
      ? `${staticSystemInstructions.length} static instruction${staticSystemInstructions.length === 1 ? "" : "s"} included`
      : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Modal
      title="Event Transcript"
      subtitle={`${streamLabel} · ${transcriptEntries.length} entries`}
      meta={transcriptMeta}
      icon={
        <span className="flex h-8 w-8 items-center justify-center border border-emerald-500/20 bg-emerald-500/10 text-emerald-500">
          <TranscriptIcon />
        </span>
      }
      onClose={onClose}
      useMotion
      widthClass="max-w-4xl"
      panelClassName="border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900"
      headerClassName="border-b border-slate-200 px-4 py-3 md:px-6 md:py-4 dark:border-slate-700"
      bodyClassName="custom-scrollbar p-3 md:p-6"
      overlayMotionProps={{
        animate: { opacity: 1 },
        exit: { opacity: 0 },
        initial: { opacity: 0 },
        transition: { duration: 0.2 },
      }}
      panelMotionProps={{
        animate: { opacity: 1, scale: 1, y: 0 },
        exit: { opacity: 0, scale: 0.98, y: 8 },
        initial: { opacity: 0, scale: 0.98, y: 8 },
        transition: { duration: 0.18 },
      }}
    >
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3 border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800/60 sm:items-center">
        <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
          {isEventTypeFilterActive && (
            <>
              <Button
                type="button"
                variant={mode === "filtered" ? "secondary" : "ghost"}
                size="sm"
                onClick={() => setMode("filtered")}
                className="flex-1 sm:flex-none"
              >
                Current Filter
              </Button>
              <Button
                type="button"
                variant={mode === "all" ? "secondary" : "ghost"}
                size="sm"
                onClick={() => setMode("all")}
                className="flex-1 sm:flex-none"
              >
                All Events
              </Button>
            </>
          )}
          {staticSystemInstructions.length > 0 && (
            <Button
              type="button"
              variant={includeStaticSystemInstructions ? "secondary" : "ghost"}
              size="sm"
              aria-pressed={includeStaticSystemInstructions}
              onClick={() =>
                setIncludeStaticSystemInstructions((current) => !current)
              }
              className="w-full justify-center sm:w-auto"
              leftIcon={<StaticInstructionIcon />}
            >
              Static Instructions
            </Button>
          )}
        </div>

        <div className="flex w-full items-center justify-between gap-2 sm:w-auto sm:justify-start">
          <span className="min-w-0 flex-1 font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400 sm:flex-none">
            {status === "copied" ? "Transcript copied" : "Copy transcript"}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void copy(transcriptText)}
            className="shrink-0"
          >
            Copy
          </Button>
        </div>
      </div>

      <div className="space-y-3">
        {transcriptEntries.map((entry, index) => {
          if (entry.kind === "static-system-instruction") {
            const { instruction } = entry;

            return (
              <article
                key={`static-system-instruction-${instruction.phase}-${instruction.stepNumber}-${index}`}
                className="overflow-hidden border border-amber-200 bg-white shadow-sm dark:border-amber-500/30 dark:bg-arti-background"
              >
                <div className="flex flex-wrap items-start gap-2 border-b border-amber-200 bg-amber-50 px-3 py-2 dark:border-amber-500/30 dark:bg-amber-500/10 sm:items-center">
                  <span className="border border-amber-200 bg-white px-1.5 py-0.5 font-mono text-[10px] font-bold text-amber-700 dark:border-amber-500/30 dark:bg-arti-background dark:text-amber-300">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <span className="flex h-5 w-5 items-center justify-center rounded-none border border-white/30 bg-amber-500 text-white shadow-sm dark:bg-amber-400 dark:text-arti-background">
                    <StaticInstructionIcon />
                  </span>
                  <span className="min-w-0 flex-1 font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-amber-700 dark:text-amber-200 sm:flex-none">
                    <span className="block truncate">
                      STATIC::SYSTEM::INSTRUCTION
                    </span>
                  </span>
                  <span className="border border-amber-200 bg-white px-1.5 py-0.5 font-mono text-[10px] font-bold tracking-[0.18em] text-amber-700 dark:border-amber-500/30 dark:bg-arti-background dark:text-amber-200">
                    MODE_LEVEL
                  </span>
                  <span className="border border-emerald-300 bg-emerald-50 px-1.5 py-0.5 font-mono text-[10px] font-bold tracking-[0.18em] text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300">
                    STEP_{instruction.stepNumber}
                  </span>
                  <div className="flex w-full flex-wrap items-center gap-2 sm:ml-auto sm:w-auto">
                    <span className="font-mono text-[10px] text-amber-700/80 dark:text-amber-200/80">
                      {formatRelativeTime(instruction.receivedAt)}
                    </span>
                    <span className="font-mono text-[10px] text-amber-700/60 dark:text-amber-200/60">
                      {formatDateTime(
                        instruction.timestamp ?? instruction.receivedAt,
                      )}
                    </span>
                  </div>
                </div>

                <div className="space-y-3 px-3 py-3">
                  <p className="font-mono text-xs leading-relaxed text-amber-800 dark:text-amber-100">
                    Static system instruction captured for the full mode.
                  </p>

                  <TranscriptInstructionBlock
                    body={instruction.body}
                    label="Instruction"
                  />
                </div>
              </article>
            );
          }

          const { event } = entry;
          const summary = getEventSummary(event);
          const injectedMessageBody = getInjectedMessageBody(event);
          const hasPayload = Object.keys(event.event_data).length > 0;

          return (
            <article
              key={`${event.received_at}-${index}`}
              className="overflow-hidden border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-arti-background"
            >
              <div className="flex flex-wrap items-start gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2 dark:border-slate-700 dark:bg-slate-900/70 sm:items-center">
                <span className="border border-slate-200 bg-white px-1.5 py-0.5 font-mono text-[10px] font-bold text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <span
                  className={`flex h-5 w-5 items-center justify-center rounded-none border border-white/30 shadow-sm ${getColorClassForEvent(
                    event.event_type,
                  )}`}
                >
                  <EventIcon type={event.event_type} />
                </span>
                <span className="min-w-0 flex-1 font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-slate-600 dark:text-slate-300 sm:flex-none">
                  <span className="block truncate">
                    {formatEventTypeLabel(event.event_type)}
                  </span>
                </span>
                {event.stepNumber !== null && (
                  <span className="border border-emerald-300 bg-emerald-50 px-1.5 py-0.5 font-mono text-[10px] font-bold tracking-[0.18em] text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300">
                    STEP_{event.stepNumber}
                  </span>
                )}
                <div className="flex w-full flex-wrap items-center gap-2 sm:ml-auto sm:w-auto">
                  <span className="font-mono text-[10px] text-slate-500 dark:text-slate-400">
                    {formatRelativeTime(event.received_at)}
                  </span>
                  <span className="font-mono text-[10px] text-slate-400 dark:text-slate-500">
                    {formatDateTime(event.timestamp ?? event.received_at)}
                  </span>
                </div>
              </div>

              <div className="space-y-3 px-3 py-3">
                {summary && injectedMessageBody === null && (
                  <p className="font-mono text-xs leading-relaxed text-slate-700 dark:text-slate-200">
                    {summary}
                  </p>
                )}

                {injectedMessageBody && (
                  <TranscriptInstructionBlock
                    body={injectedMessageBody}
                    label="Message"
                    tone="sky"
                  />
                )}

                {hasPayload && (
                  <details className="group overflow-hidden border border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-900/70">
                    <summary className="cursor-pointer select-none px-3 py-2 font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 transition-colors hover:text-emerald-600 dark:text-slate-400 dark:hover:text-emerald-300">
                      Payload
                    </summary>
                    <pre className="custom-scrollbar overflow-auto border-t border-slate-200 bg-white p-3 font-mono text-[10px] leading-relaxed text-slate-700 dark:border-slate-700 dark:bg-arti-panel dark:text-slate-200">
                      {JSON.stringify(event.event_data, null, 2)}
                    </pre>
                  </details>
                )}
              </div>
            </article>
          );
        })}

        {transcriptEntries.length === 0 && (
          <EmptyState
            size="sm"
            title="No transcript entries"
            description="No transcript entries match this view."
          />
        )}
      </div>
    </Modal>
  );
};
